import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Building2, MapPin, Users, Calendar, Clock, Filter, Loader2, Check, X, ChevronDown, ChevronUp, Eye, Plus, Pencil, UserCheck, Archive } from 'lucide-react';
import PostAnonAccountPrompt from '../components/PostAnonAccountPrompt';

const statusLabels = {
  ouvert: 'Ouvert',
  en_cours: 'En cours',
  complet: 'Complet',
  archive: 'Archivé'
};

const statusColors = {
  ouvert: 'bg-green-100 text-green-800',
  en_cours: 'bg-yellow-100 text-yellow-800',
  complet: 'bg-blue-100 text-blue-800',
  archive: 'bg-gray-100 text-gray-800'
};

const CooperativeCard = ({ cooperative, sites, onParticipate, onEdit, onViewParticipants, onArchive, userParticipations, isAgentOrAdmin }) => {
  const [expanded, setExpanded] = useState(false);
  const site = sites.find(s => s.site_id === cooperative.site_id);
  // Check if user has a pending (valide === null) or validated participation
  const hasParticipation = userParticipations.some(p => 
    p.cooperative_id === cooperative.cooperative_id && p.valide !== false
  );
  const totalPrice = cooperative.prix_parcelle;
  const cautionPct = cooperative.caution_pourcentage ?? 25;
  const acompte = totalPrice * (cautionPct / 100);
  
  const shortDesc = cooperative.description?.slice(0, 120);
  const needsExpand = cooperative.description?.length > 120;

  return (
    <Card className="card-hover bg-card border-none rounded-2xl overflow-hidden" data-testid={`cooperative-card-${cooperative.cooperative_id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-xl font-serif">{cooperative.titre}</CardTitle>
          <Badge className={statusColors[cooperative.statut]}>
            {statusLabels[cooperative.statut]}
          </Badge>
        </div>
        {/* Admin/Agent buttons */}
        {isAgentOrAdmin && (
          <div className="flex flex-wrap gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => onEdit(cooperative)}>
              <Pencil className="w-3 h-3 mr-1" /> Modifier
            </Button>
            <Button size="sm" variant="outline" onClick={() => onViewParticipants(cooperative)}>
              <UserCheck className="w-3 h-3 mr-1" /> Participants
            </Button>
            {cooperative.statut !== 'archive' && (
              <Button size="sm" variant="outline" className="text-orange-600" onClick={() => onArchive(cooperative)}>
                <Archive className="w-3 h-3 mr-1" /> {cooperative.statut === 'complet' ? 'Archiver' : 'Clôturer'}
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground">
          {expanded ? cooperative.description : shortDesc}
          {needsExpand && !expanded && '...'}
          {needsExpand && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="ml-2 text-primary hover:underline inline-flex items-center gap-1"
            >
              {expanded ? <>Voir moins <ChevronUp className="w-3 h-3" /></> : <>Voir plus <ChevronDown className="w-3 h-3" /></>}
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <span>{site?.ville || 'Non spécifié'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            <span>{cooperative.participants_count || 0} / {cooperative.nombre_parcelles}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            <span>{cooperative.duree_mois} mois</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <span>{cooperative.mensualite?.toLocaleString()} FCFA/mois</span>
          </div>
        </div>

        <div className="pt-3 border-t border-border">
          <div className="flex justify-between items-center text-sm">
            <span className="text-muted-foreground">Surface parcelle</span>
            <span className="font-semibold">{cooperative.surface_parcelle} m²</span>
          </div>
          <div className="flex justify-between items-center text-sm mt-1">
            <span className="text-muted-foreground">Prix parcelle</span>
            <span className="font-semibold text-primary">{totalPrice?.toLocaleString()} FCFA</span>
          </div>
          <div className="flex justify-between items-center text-sm mt-1">
            <span className="text-muted-foreground">Caution ({cautionPct}%)</span>
            <span className="font-semibold text-secondary">{acompte?.toLocaleString()} FCFA</span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-0 flex gap-2">
        {site && (
          <Link to={`/sites/${site.site_id}`} className="flex-1">
            <Button variant="outline" className="w-full" data-testid={`view-site-btn-${cooperative.cooperative_id}`}>
              <Eye className="w-4 h-4 mr-2" />
              Voir site
            </Button>
          </Link>
        )}
        <Button 
          className="flex-1 btn-pill"
          disabled={cooperative.statut !== 'ouvert' || hasParticipation}
          onClick={() => onParticipate(cooperative)}
          data-testid={`participate-btn-${cooperative.cooperative_id}`}
        >
          {hasParticipation ? 'En attente' : 'Participer'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default function Cooperatives() {
  const navigate = useNavigate();
  const { user, token, isAgentOrAdmin } = useAuth();
  const [cooperatives, setCooperatives] = useState([]);
  const [sites, setSites] = useState([]);
  const [participations, setParticipations] = useState([]);
  const [allParticipations, setAllParticipations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participating, setParticipating] = useState(false);
  const [selectedCooperative, setSelectedCooperative] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [selectedForParticipants, setSelectedForParticipants] = useState(null);
  
  // Create/Edit cooperative dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCooperative, setEditingCooperative] = useState(null);
  const [formData, setFormData] = useState({
    titre: '', description: '', site_id: '', duree_mois: '', mensualite: '',
    nombre_parcelles: '', prix_parcelle: '', surface_parcelle: '', type_document: 'titre_foncier', statut: 'ouvert',
    caution_pourcentage: '25'
  });
  
  // Anonymous participation
  const [anonDialogOpen, setAnonDialogOpen] = useState(false);
  const [anonData, setAnonData] = useState({ nom: '', prenom: '', email: '', telephone: '' });
  const [postPromptOpen, setPostPromptOpen] = useState(false);
  const [postPromptUserExists, setPostPromptUserExists] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    statut: '', site_id: '', prix_min: '', prix_max: '', duree_min: '', duree_max: ''
  });

  const fetchData = async () => {
    try {
      const [coopRes, sitesRes] = await Promise.all([
        fetch(`${API_URL}/api/cooperatives`),
        fetch(`${API_URL}/api/sites`)
      ]);
      
      setCooperatives(await coopRes.json());
      setSites(await sitesRes.json());

      if (user) {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const partRes = await fetch(`${API_URL}/api/participations-cooperative`, { headers, credentials: 'include' });
        if (partRes.ok) {
          const partData = await partRes.json();
          setParticipations(partData.filter(p => p.client_id === user.user_id));
          if (isAgentOrAdmin()) setAllParticipations(partData);
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [user, token]);

  const handleParticipate = (cooperative) => {
    setSelectedCooperative(cooperative);
    if (!user) {
      setAnonDialogOpen(true);
    } else {
      setDialogOpen(true);
    }
  };

  const handleEdit = (cooperative) => {
    setEditingCooperative(cooperative);
    setFormData({
      titre: cooperative.titre || '',
      description: cooperative.description || '',
      site_id: cooperative.site_id || '',
      duree_mois: cooperative.duree_mois?.toString() || '',
      mensualite: cooperative.mensualite?.toString() || '',
      nombre_parcelles: cooperative.nombre_parcelles?.toString() || '',
      prix_parcelle: cooperative.prix_parcelle?.toString() || '',
      surface_parcelle: cooperative.surface_parcelle?.toString() || '',
      type_document: cooperative.type_document || 'titre_foncier',
      statut: cooperative.statut || 'ouvert',
      caution_pourcentage: (cooperative.caution_pourcentage ?? 25).toString()
    });
    setEditDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingCooperative(null);
    setFormData({
      titre: '', description: '', site_id: '', duree_mois: '', mensualite: '',
      nombre_parcelles: '', prix_parcelle: '', surface_parcelle: '', type_document: 'titre_foncier', statut: 'ouvert',
      caution_pourcentage: '25'
    });
    setEditDialogOpen(true);
  };

  const handleArchive = async (cooperative) => {
    const newStatut = cooperative.statut === 'complet' ? 'archive' : 'complet';
    const action = newStatut === 'archive' ? 'archiver' : 'clôturer';
    if (!window.confirm(`Voulez-vous ${action} cette coopérative ?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/cooperatives/${cooperative.cooperative_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...cooperative, statut: newStatut })
      });
      if (!response.ok) throw new Error('Erreur');
      toast.success(`Coopérative ${action === 'archiver' ? 'archivée' : 'clôturée'}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSaveCooperative = async () => {
    setParticipating(true);
    try {
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
      const body = {
        ...formData,
        duree_mois: parseInt(formData.duree_mois),
        mensualite: parseFloat(formData.mensualite),
        nombre_parcelles: parseInt(formData.nombre_parcelles),
        prix_parcelle: parseFloat(formData.prix_parcelle),
        surface_parcelle: parseFloat(formData.surface_parcelle),
        caution_pourcentage: parseFloat(formData.caution_pourcentage) || 25
      };
      
      const url = editingCooperative 
        ? `${API_URL}/api/cooperatives/${editingCooperative.cooperative_id}`
        : `${API_URL}/api/cooperatives`;
      
      const response = await fetch(url, {
        method: editingCooperative ? 'PUT' : 'POST',
        headers, body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Erreur');
      toast.success(editingCooperative ? 'Coopérative mise à jour' : 'Coopérative créée');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setParticipating(false);
    }
  };

  const handleViewParticipants = (cooperative) => {
    setSelectedForParticipants(cooperative);
    setParticipantsDialogOpen(true);
  };

  const handleValidateParticipation = async (participationId, valide) => {
    try {
      const response = await fetch(`${API_URL}/api/participations-cooperative/${participationId}/${valide ? 'valider' : 'refuser'}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur');
      toast.success(valide ? 'Participation validée' : 'Participation refusée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleConfirmParticipation = async () => {
    if (!selectedCooperative) return;
    setParticipating(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${API_URL}/api/cooperatives/${selectedCooperative.cooperative_id}/participer`, {
        method: 'POST', headers, credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur');
      }

      toast.success('Demande de participation envoyée');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setParticipating(false);
    }
  };

  const handleAnonParticipation = async () => {
    setParticipating(true);
    try {
      const response = await fetch(`${API_URL}/api/cooperatives/${selectedCooperative.cooperative_id}/participer-anonyme`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(anonData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur');
      }
      const data = await response.json();
      setAnonDialogOpen(false);
      setPostPromptUserExists(!!data.user_exists);
      setPostPromptOpen(true);
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'envoi');
    } finally {
      setParticipating(false);
    }
  };

  const filteredCooperatives = cooperatives.filter(c => {
    if (filters.statut && c.statut !== filters.statut) return false;
    if (filters.site_id && c.site_id !== filters.site_id) return false;
    if (filters.prix_min && c.prix_parcelle < parseInt(filters.prix_min)) return false;
    if (filters.prix_max && c.prix_parcelle > parseInt(filters.prix_max)) return false;
    return true;
  });

  const participantsForSelected = selectedForParticipants 
    ? allParticipations.filter(p => p.cooperative_id === selectedForParticipants.cooperative_id)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-bold font-serif">Coopératives</h1>
              <p className="text-muted-foreground">Devenez propriétaire en toute sérénité</p>
            </div>
          </div>
          
          {isAgentOrAdmin() && (
            <Button onClick={handleCreate} className="btn-pill">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une coopérative
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-8 p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs mb-1 block">Statut</Label>
              <Select value={filters.statut} onValueChange={(v) => setFilters({...filters, statut: v === 'all' ? '' : v})}>
                <SelectTrigger><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs mb-1 block">Site</Label>
              <Select value={filters.site_id} onValueChange={(v) => setFilters({...filters, site_id: v === 'all' ? '' : v})}>
                <SelectTrigger><MapPin className="w-4 h-4 mr-2" /><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les sites</SelectItem>
                  {sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.nom || s.ville}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCooperatives.map(cooperative => (
            <CooperativeCard
              key={cooperative.cooperative_id}
              cooperative={cooperative}
              sites={sites}
              onParticipate={handleParticipate}
              onEdit={handleEdit}
              onViewParticipants={handleViewParticipants}
              onArchive={handleArchive}
              userParticipations={participations}
              isAgentOrAdmin={isAgentOrAdmin()}
            />
          ))}
        </div>

        {filteredCooperatives.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Aucune coopérative trouvée</p>
          </div>
        )}

        {/* Participation Dialog (logged in) */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmer la participation</DialogTitle>
              <DialogDescription>
                Vous souhaitez participer à la coopérative "{selectedCooperative?.titre}" ?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleConfirmParticipation} disabled={participating}>
                {participating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Anonymous Participation Dialog */}
        <Dialog open={anonDialogOpen} onOpenChange={setAnonDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Vos coordonnées</DialogTitle>
              <DialogDescription>Renseignez vos informations pour participer à cette coopérative.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Prénom *</Label><Input value={anonData.prenom} onChange={(e) => setAnonData({...anonData, prenom: e.target.value})} data-testid="anon-prenom-input" /></div>
                <div><Label>Nom *</Label><Input value={anonData.nom} onChange={(e) => setAnonData({...anonData, nom: e.target.value})} data-testid="anon-nom-input" /></div>
              </div>
              <div><Label>Email *</Label><Input type="email" value={anonData.email} onChange={(e) => setAnonData({...anonData, email: e.target.value})} data-testid="anon-email-input" /></div>
              <div><Label>Téléphone *</Label><Input value={anonData.telephone} onChange={(e) => setAnonData({...anonData, telephone: e.target.value})} data-testid="anon-telephone-input" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAnonDialogOpen(false)}>Annuler</Button>
              <Button
                onClick={handleAnonParticipation}
                disabled={participating || !anonData.nom || !anonData.prenom || !anonData.email || !anonData.telephone}
                data-testid="submit-anon-cooperative-btn"
              >
                {participating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Envoyer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <PostAnonAccountPrompt
          open={postPromptOpen}
          onOpenChange={setPostPromptOpen}
          userExists={postPromptUserExists}
          prefillNom={anonData.nom}
          prefillPrenom={anonData.prenom}
          prefillEmail={anonData.email}
          prefillTelephone={anonData.telephone}
        />

        {/* Edit/Create Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCooperative ? 'Modifier la coopérative' : 'Nouvelle coopérative'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div><Label>Titre *</Label><Input value={formData.titre} onChange={(e) => setFormData({...formData, titre: e.target.value})} /></div>
              <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Site *</Label>
                  <Select value={formData.site_id} onValueChange={(v) => setFormData({...formData, site_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.nom || s.ville}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={formData.statut} onValueChange={(v) => setFormData({...formData, statut: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Durée (mois)</Label><Input type="number" value={formData.duree_mois} onChange={(e) => setFormData({...formData, duree_mois: e.target.value})} /></div>
                <div><Label>Mensualité (FCFA)</Label><Input type="number" value={formData.mensualite} onChange={(e) => setFormData({...formData, mensualite: e.target.value})} /></div>
                <div><Label>Nb parcelles</Label><Input type="number" value={formData.nombre_parcelles} onChange={(e) => setFormData({...formData, nombre_parcelles: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Prix parcelle (FCFA)</Label><Input type="number" value={formData.prix_parcelle} onChange={(e) => setFormData({...formData, prix_parcelle: e.target.value})} /></div>
                <div><Label>Surface parcelle (m²)</Label><Input type="number" value={formData.surface_parcelle} onChange={(e) => setFormData({...formData, surface_parcelle: e.target.value})} /></div>
              </div>
              <div>
                <Label>Caution à la participation (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={formData.caution_pourcentage}
                  onChange={(e) => setFormData({...formData, caution_pourcentage: e.target.value})}
                  data-testid="cooperative-caution-input"
                  placeholder="25"
                />
                <p className="text-xs text-muted-foreground mt-1">Pourcentage du prix de la parcelle demandé en caution (défaut 25%).</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSaveCooperative} disabled={participating}>
                {participating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingCooperative ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Participants Dialog */}
        <Dialog open={participantsDialogOpen} onOpenChange={setParticipantsDialogOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Participants - {selectedForParticipants?.titre}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {participantsForSelected.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">Aucun participant</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Téléphone</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {participantsForSelected.map(p => (
                      <TableRow key={p.participation_id}>
                        <TableCell>{p.client_nom || 'N/A'}</TableCell>
                        <TableCell>{p.client_email || 'N/A'}</TableCell>
                        <TableCell>{p.client_telephone || 'N/A'}</TableCell>
                        <TableCell>{new Date(p.created_at).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>
                          <Badge className={p.valide ? 'bg-green-100 text-green-800' : p.valide === false ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                            {p.valide ? 'Validé' : p.valide === false ? 'Refusé' : 'En attente'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {p.valide === null && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleValidateParticipation(p.participation_id, true)}><Check className="w-4 h-4" /></Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleValidateParticipation(p.participation_id, false)}><X className="w-4 h-4" /></Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
